import React, { useState } from 'react';
import Header from '@/components/layout/Header';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Film, Music, Check, Upload, Clock, DollarSign, Award, Calendar } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const Sync: React.FC = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('opportunities');
  
  const handleSubmit = () => {
    toast({
      title: "Submission successful",
      description: "Your music has been submitted for sync consideration",
    });
  };
  
  const syncOpportunities = [
    {
      id: 1,
      title: "Indie Film Soundtrack",
      description: "Looking for atmospheric indie tracks for upcoming drama film",
      deadline: "Jul 30, 2023",
      budget: "$500-1,500",
      genre: "Indie, Alternative, Ambient",
      type: "Film"
    },
    {
      id: 2,
      title: "Mobile Game Background Music",
      description: "Upbeat electronic tracks needed for casual mobile game",
      deadline: "Aug 15, 2023",
      budget: "$300-800 per track",
      genre: "Electronic, Pop, Instrumental",
      type: "Game"
    },
    {
      id: 3,
      title: "TV Commercial Campaign",
      description: "Inspirational tracks for major sportswear brand campaign",
      deadline: "Jul 28, 2023",
      budget: "$2,000-5,000",
      genre: "Pop, Rock, Hip-Hop",
      type: "Commercial"
    }
  ];

  const submittedTracks = [
    {
      id: 1,
      title: "Midnight Dreams",
      opportunity: "Netflix Series",
      submittedDate: "Jun 15, 2023",
      status: "Under Review"
    },
    {
      id: 2,
      title: "Urban Pulse",
      opportunity: "Sports Documentary",
      submittedDate: "Jun 02, 2023",
      status: "Selected"
    }
  ];
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-28 px-4 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 animate-fade-in">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold flex items-center gap-3 bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
              <Film className="h-10 w-10 text-indigo-500" />
              Music Sync Opportunities
            </h1>
            <p className="text-muted-foreground text-lg">Submit your music for film, TV, ads, games and more</p>
          </div>
          <Button className="gap-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
            <Upload className="h-4 w-4" />
            Submit Music
          </Button>
        </div>
        
        <Tabs defaultValue="opportunities" value={activeTab} onValueChange={setActiveTab} className="animate-slide-up">
          <TabsList className="grid w-full md:w-[400px] grid-cols-2">
            <TabsTrigger value="opportunities">Opportunities</TabsTrigger>
            <TabsTrigger value="submitted">My Submissions</TabsTrigger>
          </TabsList>
          
          <TabsContent value="opportunities" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {syncOpportunities.map((opportunity) => (
                <Card key={opportunity.id} className="overflow-hidden border-t-4 border-t-indigo-500 animate-fade-in">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>{opportunity.title}</CardTitle>
                      <div className="px-2 py-1 bg-muted rounded text-xs font-medium">{opportunity.type}</div>
                    </div>
                    <CardDescription>{opportunity.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>Deadline: {opportunity.deadline}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span>Budget: {opportunity.budget}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Music className="h-4 w-4 text-muted-foreground" />
                        <span>Genre: {opportunity.genre}</span>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button className="w-full" onClick={handleSubmit}>Submit Your Music</Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="submitted" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {submittedTracks.map((track) => (
                <Card key={track.id} className="overflow-hidden animate-fade-in">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>{track.title}</CardTitle>
                      <div className={`px-2 py-1 rounded text-xs font-medium ${
                        track.status === "Selected" 
                          ? "bg-green-500/20 text-green-500" 
                          : "bg-amber-500/20 text-amber-500"
                      }`}>
                        {track.status}
                      </div>
                    </div>
                    <CardDescription>Submitted for: {track.opportunity}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>Submitted: {track.submittedDate}</span>
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-between">
                    <Button variant="outline">View Details</Button>
                    {track.status === "Selected" ? (
                      <Button className="gap-2 bg-green-500 hover:bg-green-600">
                        <Check className="h-4 w-4" />
                        Accept Offer
                      </Button>
                    ) : (
                      <Button variant="outline">Cancel Submission</Button>
                    )}
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Sync;
